// eslint-disable-next-line import/no-extraneous-dependencies
/* eslint-disable max-lines */
import { ChevronRight, Copy, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import React, { FC, Fragment, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Faqs } from 'src/components/Faqs';
import { useGetFaqsFromFirestore } from 'src/queries';
import { useQueryHelper } from 'src/queries/utils';

import Stared from '../../assets/icons/stared.svg';
import Trustmate from '../../assets/icons/trustmate.png';
import GoogleIcon from '../../assets/images/icon_google.png';
import Tripadvisor from '../../assets/images/icon_tripadvisor.png';
import { Error, Flex, Modal } from '../../components';
import { useToast } from '../../hooks';
import { useHotelState, useOrganizationState } from '../../state';
import { AttractionBlock, BusinessCards, City, HomeMenu, Hotel } from './components';
import {
  ButtonContainer,
  HomeContainer,
  OpinionIcon,
  SocialMediaButton,
  SocialMediaContainer,
  SocialMediaText,
  WifiContainer,
  WifiText,
  WifiTextTitle,
} from './Home.styled';

export const Home: FC = () => {
  const { t } = useTranslation();
  const { organizationData } = useOrganizationState();
  const [modalPartnerProgramInfo, setModalPartnerProgramInfo] = useState(false);
  const [modalWifiInfo, setModalWifiInfo] = useState(false);
  const [modalVirtualTour, setModalVirtualTour] = useState(false);
  const [modalSocialMedia, setModalSocialMedia] = useState(false);
  const [modalOpinion, setModalOpinion] = useState(false);
  const AssociatedAttractions = organizationData.associated_attractions || [];
  const { hotelData } = useHotelState();
  const { showInfo } = useToast();
  const faq = useGetFaqsFromFirestore(hotelData.id);
  const { data: faqsData } = faq;

  const { isLoading, isError } = useQueryHelper({
    queries: [
      faq,
    ],
  });

  return (
    <>
      <Helmet>
        <title>Hotelspot | {hotelData.name}</title>
        <meta
          name="description"
          content="Intuicyjna aplikacja hotelspot cyfrowy concierge dla hoteli,
        umożliwiająca gościom pełen dostęp do różnorodnych usług i informacji, poprawiając komfort i wygodę ich pobytu."
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content="Hotelspot | Strona główna" />
        <meta
          property="og:description"
          content="Intuicyjna aplikacja hotelspot cyfrowy concierge dla hoteli,
          umożliwiająca gościom pełen dostęp do różnorodnych usług i informacji."
        />

        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content={window.location.href} />
        <meta property="twitter:title" content="Hotelspot | Strona główna" />
        <meta
          property="twitter:description"
          content="Intuicyjna aplikacja hotelspot cyfrowy concierge dla hoteli,
          umożliwiająca gościom pełen dostęp do różnorodnych usług i informacji."
        />
      </Helmet>
      <HomeContainer>
        <Hotel />
        <BusinessCards id={organizationData.hotel} />
        <HomeMenu
          showPartnerProgramInfo={() => setModalPartnerProgramInfo(!modalPartnerProgramInfo)}
          showWifiInfo={() => setModalWifiInfo(!modalWifiInfo)}
          showVirtualTour={() => setModalVirtualTour(!modalVirtualTour)}
          showSocialMedia={() => setModalSocialMedia(!modalSocialMedia)}
          showOpinions={() => setModalOpinion(!modalOpinion)}
        />
        <City id={organizationData.city} />
        <AttractionBlock filterKey={AssociatedAttractions} type="TRIP" />
        <AttractionBlock filterKey={AssociatedAttractions} type="FORKIDS" />
        <AttractionBlock filterKey={AssociatedAttractions} type="FOODS" />
        <AttractionBlock filterKey={AssociatedAttractions} type="CULTURE" />
        <Faqs isError={isError} faqs={faqsData} isLoading={isLoading} />
        <Modal
          isOpen={modalPartnerProgramInfo}
          onClose={() => setModalPartnerProgramInfo(false)}
          title={t('menu_items.loyalty')}
        >
          {organizationData.loyalty_program_url ? (
            <SocialMediaContainer key="modal">
              <ButtonContainer
                href={organizationData.loyalty_program_url}
                target="_blank"
              >
                <OpinionIcon src={Stared} />
                <SocialMediaText>{t('loyalty_program.join')}</SocialMediaText>
                <ChevronRight size={24} color="#1C1C1C" />
              </ButtonContainer>
            </SocialMediaContainer>
          ) : (
            <SocialMediaContainer key="modal">
              <Error info errorTitle="Brak danych o opinii" />
            </SocialMediaContainer>
          )}
        </Modal>
        <Modal
          isOpen={modalWifiInfo}
          onClose={() => setModalWifiInfo(false)}
          title={t('menu_items.wifi')}
        >
          {organizationData.wifiSettings ? (
            <WifiContainer key="modal">
              <ButtonContainer>
                <WifiTextTitle>{t('form.name')}:</WifiTextTitle>
                <WifiText>{organizationData.wifiSettings.ssid}</WifiText>
              </ButtonContainer>
              {organizationData?.wifiSettings?.password && organizationData.wifiSettings.password !== '' && (
              <ButtonContainer>
                <WifiTextTitle>{t('form.password')}:</WifiTextTitle>
                <CopyToClipboard
                  text={organizationData.wifiSettings.password}
                  onCopy={() => {
                    setModalWifiInfo(false);
                    showInfo('Hasło skopiowane do schowka!');
                  }}
                >
                  <WifiText>
                    <Flex gap="10px">{organizationData.wifiSettings.password} <Copy /></Flex>
                  </WifiText>
                </CopyToClipboard>
              </ButtonContainer>
              )}
            </WifiContainer>
          ) : (
            <WifiContainer key="modal">
              <Error
                errorTitle="Brak danych o Wi-Fi"
                info
              />
            </WifiContainer>
          )}
        </Modal>

        <Modal
          isOpen={modalSocialMedia}
          onClose={() => setModalSocialMedia(false)}
          title="Social Media"
        >
          {organizationData.socialMediaSettings ? (
            <SocialMediaContainer key="modal">
              {organizationData.socialMediaSettings?.instagramUrl && (
              <SocialMediaButton
                href={organizationData.socialMediaSettings?.instagramUrl}
                target="_blank"
              >
                <Instagram strokeWidth={1.5} size={24} color="#1C1C1C" />
                <SocialMediaText>Intagram</SocialMediaText>
                <ChevronRight size={24} color="#1C1C1C" />
              </SocialMediaButton>
              )}
              { organizationData.socialMediaSettings?.facebookUrl && (
              <SocialMediaButton
                href={organizationData.socialMediaSettings?.facebookUrl}
                target="_blank"
              >
                <Facebook strokeWidth={1.5} size={24} color="#1c1c1c" />
                <SocialMediaText>Facebook</SocialMediaText>
                <ChevronRight size={24} color="#1C1C1C" />
              </SocialMediaButton>
              )}
              { organizationData.socialMediaSettings?.linkedInUrl && (
              <SocialMediaButton
                href={organizationData.socialMediaSettings?.linkedInUrl}
                target="_blank"
              >
                <Linkedin strokeWidth={1.5} size={24} color="#1C1C1C" />
                <SocialMediaText>LinkedIn</SocialMediaText>
                <ChevronRight size={24} color="#1C1C1C" />
              </SocialMediaButton>
              )}
              { organizationData.socialMediaSettings?.youTubeUrl && (
              <SocialMediaButton
                href={organizationData.socialMediaSettings?.youTubeUrl}
                target="_blank"
              >
                <Youtube strokeWidth={1.5} size={24} color="#1C1C1C" />
                <SocialMediaText>YouTube</SocialMediaText>
                <ChevronRight size={24} color="#1C1C1C" />
              </SocialMediaButton>
              )}
            </SocialMediaContainer>
          ) : (
            <SocialMediaContainer key="modal">
              <Error info errorTitle="Brak mediów społecznościowych" />
            </SocialMediaContainer>
          )}
        </Modal>
        <Modal
          size="full"
          isOpen={modalVirtualTour}
          onClose={() => setModalVirtualTour(false)}
          title={t('menu_items.virtual_tour')}
        >
          <Fragment key="modal">
            <iframe
              title="virtual-tour"
              width="100%"
              style={{ height: 'calc(100vh - 65px)' }}
              src={organizationData.virtual_tour_url || ''}
            />
          </Fragment>
        </Modal>
        <Modal
          isOpen={modalOpinion}
          onClose={() => setModalOpinion(false)}
          title={t('menu_items.opinion')}
        >
          {organizationData.opinionServicesSettings ? (
            <SocialMediaContainer key="modal">
              {organizationData.opinionServicesSettings?.google && (
              <SocialMediaButton
                href={organizationData.opinionServicesSettings?.google}
                target="_blank"
              >
                <OpinionIcon src={GoogleIcon} />
                <SocialMediaText>Google Opinion</SocialMediaText>
                <ChevronRight size={24} color="#1C1C1C" />
              </SocialMediaButton>
              )}
              { organizationData.opinionServicesSettings?.tropadvisor && (
              <SocialMediaButton
                href={organizationData.opinionServicesSettings?.tropadvisor}
                target="_blank"
              >
                <OpinionIcon src={Tripadvisor} />
                <SocialMediaText>Tripadvisor</SocialMediaText>
                <ChevronRight size={24} color="#1C1C1C" />
              </SocialMediaButton>
              )}
              { organizationData.opinionServicesSettings?.trustmate && (
              <SocialMediaButton
                href={organizationData.opinionServicesSettings?.trustmate}
                target="_blank"
              >
                <OpinionIcon src={Trustmate} />
                <SocialMediaText>Trustmate</SocialMediaText>
                <ChevronRight size={24} color="#1C1C1C" />
              </SocialMediaButton>
              )}
            </SocialMediaContainer>
          ) : (
            <SocialMediaContainer key="modal">
              <Error info errorTitle="Brak danych o opinii" />
            </SocialMediaContainer>
          )}
        </Modal>
      </HomeContainer>
    </>
  );
};
